import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import type { DateRange as RDPRange } from 'react-day-picker';
import {
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react';
import './DateRangePicker.scss';
import type { DateRangePickerProps, Preset } from './DateRangePicker.types';
import {
  PRESET_LABELS,
  detectPreset,
  formatRangeLabel,
  getPresetRange,
  rangeToTuple,
  tupleToRange,
} from './DateRangePicker.utils';

const ROOT = 'date-range-picker';

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  mode = 'range',
  value,
  onChange,
  presets = [],
  minDate,
  maxDate,
  disabled = false,
  className = '',
  hasError = false,
  errorMessage,
  ariaLabel,
  id,
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Highlights the Custom pill until the user picks a day (or picks a different preset).
  const [isCustomMode, setIsCustomMode] = useState(false);
  // True between the first and second day-click of a fresh range selection.
  // While true, we mask react-day-picker's `selected` to `{from, to: undefined}`
  // so the second click extends (instead of triggering another reset).
  const [isPickingRange, setIsPickingRange] = useState(false);
  const reactId = useId();
  const labelSpanId = `${ROOT}-selection-${reactId}`;
  const ariaLabelId = ariaLabel ? `${ROOT}-label-${reactId}` : undefined;
  const labelledBy = [ariaLabelId, labelSpanId].filter(Boolean).join(' ') || undefined;

  const { refs, floatingStyles, context } = useFloating<HTMLButtonElement>({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const close = useCallback(() => setIsOpen(false), []);
  const focusTrigger = useCallback(() => {
    const node = refs.reference.current as HTMLButtonElement | null;
    node?.focus();
  }, [refs.reference]);

  // Reset the Custom-mode + in-progress-pick flags whenever the picker closes.
  useEffect(() => {
    if (!isOpen) {
      setIsCustomMode(false);
      setIsPickingRange(false);
    }
  }, [isOpen]);

  const detectedPreset = useMemo(() => detectPreset(value, presets), [value, presets]);
  const selectedPreset = isCustomMode ? 'custom' : detectedPreset;

  const hasValue = useMemo(() => {
    const [start, end] = value as [Date | null, Date | null];
    const unit = (value as unknown[])[3];
    return Boolean(start ?? end) || unit === 'allTime';
  }, [value]);

  const triggerLabel = useMemo(
    () => (hasValue ? formatRangeLabel(value, mode, presets) : placeholder ?? formatRangeLabel(value, mode, presets)),
    [hasValue, value, mode, presets, placeholder],
  );

  const handlePresetClick = (preset: Preset) => {
    if (preset === 'custom') {
      // Clear range and stay open; user will pick start, then end.
      setIsCustomMode(true);
      setIsPickingRange(false);
      onChange([null, null]);
      return;
    }
    setIsCustomMode(false);
    setIsPickingRange(false);
    const next = getPresetRange(preset);
    onChange(next);
    close();
    focusTrigger();
  };

  // Unified range-pick flow: with `resetOnSelect` on DayPicker, clicking any day
  // while a complete range is selected resets to `{from: day, to: undefined}` —
  // the picker must stay open for the user to pick the end date. Clicking again
  // extends via addToRange, producing `{from, to}` — we commit and close.
  const handleRangeSelect = (range: RDPRange | undefined) => {
    const from = range?.from;
    const to = range?.to;

    if (from && !to) {
      // First pick (fresh start). Keep open for the second pick.
      onChange(rangeToTuple(from, from));
      setIsPickingRange(true);
      return;
    }

    // Second pick (or range cleared): commit and close.
    onChange(rangeToTuple(from, to));
    setIsCustomMode(false);
    setIsPickingRange(false);
    close();
    focusTrigger();
  };

  const handleSingleSelect = (day: Date | undefined) => {
    if (!day) {
      onChange([null, null]);
      return;
    }
    onChange([day, day]);
    close();
    focusTrigger();
  };

  const rangeSelected = tupleToRange(value);
  // While mid-pick, mask the selection to `{from, to: undefined}` so the next
  // click is treated as extending (not a fresh reset).
  const dayPickerSelection = isPickingRange ? { from: rangeSelected.from, to: undefined } : rangeSelected;
  const singleSelected = (value as [Date | null, Date | null])[0] ?? undefined;
  const defaultMonth = rangeSelected.from ?? singleSelected ?? new Date();

  const rootClasses = [
    ROOT,
    className,
    disabled && `${ROOT}--disabled`,
    hasError && `${ROOT}--error`,
    isOpen && `${ROOT}--open`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      {ariaLabel && (
        <span id={ariaLabelId} className={`${ROOT}__sr-only`}>
          {ariaLabel}
        </span>
      )}
      <button
        type="button"
        ref={refs.setReference}
        className={`${ROOT}__trigger`}
        disabled={disabled}
        aria-labelledby={labelledBy}
        id={id}
        {...getReferenceProps()}
      >
        <span
          id={labelSpanId}
          className={`${ROOT}__trigger-label ${!hasValue ? `${ROOT}__trigger-label--placeholder` : ''}`}
        >
          {triggerLabel}
        </span>
        <span className={`${ROOT}__trigger-caret`} aria-hidden="true">
          ▾
        </span>
      </button>
      <span className={`${ROOT}__trigger-border`} aria-hidden="true" />

      {isOpen && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={`${ROOT}__popover`}
            aria-label={ariaLabel ?? 'Date range picker'}
            {...getFloatingProps()}
          >
            {presets.length > 0 && (
              <ul className={`${ROOT}__presets`} role="listbox">
                {presets.map((preset) => {
                  const isActive = preset === selectedPreset;
                  return (
                    <li key={preset} role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        className={`${ROOT}__preset ${isActive ? `${ROOT}__preset--active` : ''}`}
                        onClick={() => handlePresetClick(preset)}
                      >
                        {PRESET_LABELS[preset]}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className={`${ROOT}__calendar`}>
              {mode === 'range' ? (
                <DayPicker
                  mode="range"
                  numberOfMonths={2}
                  selected={dayPickerSelection}
                  onSelect={handleRangeSelect}
                  resetOnSelect
                  defaultMonth={defaultMonth}
                  disabled={
                    minDate || maxDate
                      ? ([minDate ? { before: minDate } : false, maxDate ? { after: maxDate } : false].filter(
                          Boolean,
                        ) as never)
                      : undefined
                  }
                  startMonth={minDate}
                  endMonth={maxDate}
                />
              ) : (
                <DayPicker
                  mode="single"
                  selected={singleSelected}
                  onSelect={handleSingleSelect}
                  defaultMonth={defaultMonth}
                  disabled={
                    minDate || maxDate
                      ? ([minDate ? { before: minDate } : false, maxDate ? { after: maxDate } : false].filter(
                          Boolean,
                        ) as never)
                      : undefined
                  }
                  startMonth={minDate}
                  endMonth={maxDate}
                />
              )}
            </div>
          </div>
        </FloatingPortal>
      )}

      {hasError && errorMessage && <div className={`${ROOT}__error-message`}>{errorMessage}</div>}
    </div>
  );
};

export default DateRangePicker;
